/**
 * 睡眠レポート用 Zustand ストア
 */

import { create } from 'zustand'

import {
  adviseSleepBreathing,
  analyzeSleepDay,
  createTodayAdvice,
  getSleepDay,
  getSleepDays,
  getSleepEpisodeRaw,
  getSleepPlan,
  getSleepTrends,
  getTodayAdvice,
  updateSleepSettings,
} from '@/api/services/sleep'
import type {
  SleepDayDetail,
  SleepDaySummary,
  SleepPlanResponse,
  SleepSettings,
  SleepTrendsResponse,
  TodayAdviceResponse,
} from '@/api/types/sleep'

export type EpisodeRawState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'done'; raw: Record<string, unknown> }

type SleepState = {
  days: SleepDaySummary[]
  isDaysLoading: boolean

  detail: SleepDayDetail | null
  // 表示中（読み込み中を含む）の日。前後に素早く移動したとき古い応答で上書きしないために使う
  detailDate: string | null
  isDetailLoading: boolean
  isAnalyzing: boolean

  /** 今日起きた睡眠（今日タブ用。記録タブで別の日を選んでも変わらない） */
  lastNight: SleepDayDetail | null

  trends: SleepTrendsResponse | null
  isTrendsLoading: boolean

  plan: SleepPlanResponse | null
  /** plan を取得した時刻（ms） */
  planFetchedAt: number
  isPlanLoading: boolean

  today: TodayAdviceResponse | null
  isTodayLoading: boolean
  isTodayGenerating: boolean

  /** 睡眠エピソードの Garmin 生データ（開いたものだけ取得する） */
  episodeRaws: Map<number, EpisodeRawState>

  /** SpO2・呼吸の対策を AI に頼んだ日。失敗しても同じ日は自動で頼み直さない */
  breathingRequested: Set<string>
  isAdvisingBreathing: boolean

  error: string | null

  fetchDays: () => Promise<void>
  fetchDetail: (date: string) => Promise<void>
  fetchLastNight: (date: string) => Promise<void>
  analyze: (date: string) => Promise<void>
  fetchTrends: (days: number) => Promise<void>
  fetchPlan: (cached?: boolean) => Promise<void>
  saveSettings: (settings: SleepSettings) => Promise<void>
  /** 今日の分がまだ無ければ作る */
  fetchToday: () => Promise<void>
  generateToday: () => Promise<void>
  /** 提案した TODO を時間に入れたことを画面に反映する */
  markPickScheduled: (todoId: number, start: string, end: string) => void
  /** 取得済み・取得中なら何もしない */
  fetchEpisodeRaw: (episodeId: number) => Promise<void>
  /** SpO2・呼吸の対策を AI で作る。同じ日は 1 回だけ */
  adviseBreathing: (date: string) => Promise<void>
}

const errorMessage = (error: unknown, fallback: string): string => (error instanceof Error ? error.message : fallback)

export const useSleepStore = create<SleepState>((set, get) => ({
  days: [],
  isDaysLoading: false,
  detail: null,
  detailDate: null,
  isDetailLoading: false,
  isAnalyzing: false,
  lastNight: null,
  trends: null,
  isTrendsLoading: false,
  plan: null,
  planFetchedAt: 0,
  isPlanLoading: false,
  today: null,
  isTodayLoading: false,
  isTodayGenerating: false,
  episodeRaws: new Map(),
  breathingRequested: new Set(),
  isAdvisingBreathing: false,
  error: null,

  fetchDays: async () => {
    set({ isDaysLoading: true, error: null })
    try {
      const res = await getSleepDays(60)
      set({ days: res.days })
    } catch (error) {
      set({ error: errorMessage(error, '睡眠の一覧を取得できませんでした') })
    } finally {
      set({ isDaysLoading: false })
    }
  },

  fetchDetail: async (date) => {
    set({ detailDate: date, isDetailLoading: true, error: null })
    try {
      const detail = await getSleepDay(date)
      if (get().detailDate !== date) return
      set({ detail })
    } catch (error) {
      if (get().detailDate !== date) return
      set({ error: errorMessage(error, '睡眠を取得できませんでした') })
    } finally {
      if (get().detailDate === date) set({ isDetailLoading: false })
    }
  },

  fetchLastNight: async (date) => {
    try {
      set({ lastNight: await getSleepDay(date) })
    } catch {
      // 無ければカードを出さないだけなので、画面にエラーは出さない
      set({ lastNight: null })
    }
  },

  analyze: async (date) => {
    set({ isAnalyzing: true, error: null })
    try {
      const detail = await analyzeSleepDay(date)
      if (get().detailDate === date) set({ detail })
      if (get().lastNight?.date === date) set({ lastNight: detail })
      set((state) => ({
        days: state.days.map((d) => (d.date === date ? { ...d, has_ai: true } : d)),
      }))
    } catch (error) {
      set({ error: errorMessage(error, 'AI 解析に失敗しました') })
    } finally {
      set({ isAnalyzing: false })
    }
  },

  fetchTrends: async (days) => {
    set({ isTrendsLoading: true, error: null })
    try {
      const trends = await getSleepTrends(days)
      set({ trends })
    } catch (error) {
      set({ error: errorMessage(error, '睡眠の推移を取得できませんでした') })
    } finally {
      set({ isTrendsLoading: false })
    }
  },

  fetchPlan: async (cached = false) => {
    set({ isPlanLoading: true, error: null })
    try {
      const plan = await getSleepPlan(cached)
      set({ plan, planFetchedAt: Date.now() })
    } catch (error) {
      set({ error: errorMessage(error, '睡眠プランを取得できませんでした') })
    } finally {
      set({ isPlanLoading: false })
    }
  },

  saveSettings: async (settings) => {
    // 計画の作り直しには時間がかかるので、設定だけ先に反映する
    set((s) => (s.plan ? { plan: { ...s.plan, settings } } : {}))
    try {
      await updateSleepSettings(settings)
    } catch (error) {
      set({ error: errorMessage(error, '設定を保存できませんでした') })
      return
    }
    await get().fetchPlan()
  },

  fetchToday: async () => {
    set({ isTodayLoading: true, error: null })
    try {
      const today = await getTodayAdvice()
      set({ today })
      // 提案は朝に 1 回作れば足りる。まだ無いときと、作った後に今日の起床が記録された・変わったときだけ作る
      if (today === null || today.stale) await get().generateToday()
    } catch (error) {
      set({ error: errorMessage(error, '今日の過ごし方を取得できませんでした') })
    } finally {
      set({ isTodayLoading: false })
    }
  },

  generateToday: async () => {
    if (get().isTodayGenerating) return
    set({ isTodayGenerating: true, error: null })
    try {
      const today = await createTodayAdvice()
      set({ today })
    } catch (error) {
      set({ error: errorMessage(error, '今日の過ごし方を作れませんでした') })
    } finally {
      set({ isTodayGenerating: false })
    }
  },

  markPickScheduled: (todoId, start, end) => {
    set((state) => ({
      today: state.today && {
        ...state.today,
        todo_picks: state.today.todo_picks.map((p) =>
          p.todo_id === todoId ? { ...p, scheduled_start: start, scheduled_end: end } : p,
        ),
      },
    }))
  },

  fetchEpisodeRaw: async (episodeId) => {
    const current = get().episodeRaws.get(episodeId)
    if (current && current.status !== 'error') return
    const setRaw = (state: EpisodeRawState) =>
      set((s) => ({ episodeRaws: new Map(s.episodeRaws).set(episodeId, state) }))
    setRaw({ status: 'loading' })
    try {
      const res = await getSleepEpisodeRaw(episodeId)
      setRaw({ status: 'done', raw: res.raw })
    } catch (error) {
      setRaw({ status: 'error', message: errorMessage(error, '生データを取得できませんでした') })
    }
  },

  adviseBreathing: async (date) => {
    if (get().breathingRequested.has(date)) return
    set((s) => ({ breathingRequested: new Set(s.breathingRequested).add(date), isAdvisingBreathing: true }))
    try {
      const detail = await adviseSleepBreathing(date)
      if (get().detailDate === date) set({ detail })
      if (get().lastNight?.date === date) set({ lastNight: detail })
    } catch {
      // 対策が出ないだけなので、画面にエラーは出さない（再解析で作り直せる）
    } finally {
      set({ isAdvisingBreathing: false })
    }
  },
}))
